import { useState, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import { ArrowUpRight, Banknote, ChevronLeft, Clock, Megaphone, MapPin, Plus, Receipt, Users, X } from "lucide-react";
import { Icon, Spinner } from "@cntrl/ui";
import { DetailsFrame } from "./DetailsFrame.js";
import type { components } from "@cntrl/api-client";
import { useCapability } from "../../app/bandContext.js";
import { usePhoneLayout } from "../../app/phoneLayout.js";
import {
  useHideCalendarDay,
  useMyShowExpenses,
  useOpsCalendarItem,
  useOpsCalendarMyPay,
  useRespondCalendarItem,
  type OpsCalendarItemKind,
} from "../../app/hooks/useShows.js";
import { pushToast } from "../../app/toasts.js";
import { PersonAvatar } from "../shared/PersonAvatar.js";
import { initials } from "../../shells/mobile/accountRoles.js";
import { mapsUrl } from "../mobile/dayOfShow.js";
import { formatTime12 } from "./lib/bookingRows.js";
import { AddExpenseView, SettlementView } from "./CalendarEventMoney.js";
import {
  ExpensesClosedBar,
  MoneySummaryCard,
  MoneyView,
  ReopenScopeButton,
  YourPayCard,
} from "./CalendarEventPostShow.js";
import { completedLine, useShowSettleBox } from "./lib/calendarMoney.js";
import { oppTypeLabel, prFormatLabel, prSubtypeLabel } from "../dashboard/oppDisplay.js";
import { formatDay, offerDaysLeft } from "../org/rosterFormat.js";
import { ago } from "../org/bookingFunnelCards.js";
import { slotRoleLabel } from "./sharedShowFormat.js";
import styles from "./CalendarEventDetails.module.scss";

type Detail = components["schemas"]["OpsCalendarItemDetail"];
type Person = components["schemas"]["OpsCalendarPerson"];

/** Which event is open, and the element it was opened from (desktop anchors the pop-up to it). */
export interface OpenCalendarEvent {
  kind: OpsCalendarItemKind;
  id: string;
  anchor: DOMRect | null;
}

const KIND_LABEL: Record<string, string> = {
  show: "Show", festival: "Festival", hold: "Hold", rehearsal: "Rehearsal",
  content: "Content", pr: "PR", off: "Off", travel: "Travel",
};

/** The viewer's standing, as the header pill says it. A viewer who is not on the event (a label, a manager)
 *  gets no pill — there is nothing about THEM to say. */
const STATUS_PILL: Record<string, { label: string; tone: "invited" | "going" | "itinerary" }> = {
  invited: { label: "Invited", tone: "invited" },
  going: { label: "Confirmed", tone: "going" },
  itinerary: { label: "Itinerary", tone: "itinerary" },
};

/**
 * THE CALENDAR'S EVENT DETAILS (ART-305 part 2) — read-only, with the viewer's three possible answers. Desktop gets
 * a pop-up anchored to the bar or row it was opened from; the phone gets a bottom sheet (our `Sheet`, so the
 * swipe-down, the scrim and Escape are the primitive's). Same content and actions on both.
 *
 * Everything shown is the SERVER's answer: who may open the event, the viewer's own standing and so which buttons
 * they get, who hears a back-out (its first name for the copy), and whether a Day of Show exists. Pay is the
 * viewer's own, from its own money-gated read, asked only by a seat that holds show money at some grain.
 */
export function CalendarEventDetails({ open, onClose }: { open: OpenCalendarEvent; onClose: () => void }) {
  const isMobile = usePhoneLayout();
  const q = useOpsCalendarItem(open.kind, open.id);
  const title = q.data?.title ?? "Event details";

  const body = q.isLoading ? (
    <div className={styles.loading} role="status" aria-live="polite"><Spinner /></div>
  ) : q.isError || !q.data ? (
    <div className={styles.loading}>
      <p className={styles.error}>Couldn&apos;t load this event.</p>
      <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
        <Icon icon={X} size={20} />
      </button>
    </div>
  ) : (
    <DetailsBody detail={q.data} kind={open.kind} id={open.id} onClose={onClose} isMobile={isMobile} />
  );

  return <DetailsFrame title={title} anchor={open.anchor} onClose={onClose}>{body}</DetailsFrame>;
}

type Confirm = null | "backout" | "hide";

/** ART-305 v2/v3 — the sheet's views. Add expense, Settlement and Money open INSIDE the same sheet / pop-up; the
 *  header's kind row becomes a "‹ Details" (or "‹ Money", "‹ Settlement") back button and ✕ still closes everything.
 *  Settlement can be another show's — a show on the same run, opened from Money. */
type View =
  | { view: "expense" }
  | { view: "money" }
  | { view: "settle"; show?: { showId: string; venue: string; date: string } };

const BACK_LABEL: Record<string, string> = { main: "Details", money: "Money", settle: "Settlement", expense: "Add expense" };

function DetailsBody({ detail, kind, id, onClose, isMobile }: {
  detail: Detail; kind: OpsCalendarItemKind; id: string; onClose: () => void; isMobile: boolean;
}) {
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState<Confirm>(null);
  // The sub-views opened from Details, newest last; back pops one.
  const [stack, setStack] = useState<View[]>([]);
  const push = (v: View) => setStack((cur) => [...cur, v]);
  const pop = () => setStack((cur) => cur.slice(0, -1));
  const respond = useRespondCalendarItem();
  const hide = useHideCalendarDay();

  // Pay: a seat that holds show money at SOME grain (a plain band member's grain is their own pay), on an event
  // they are on. Everyone else never asks — a label, a manager, an event you only read.
  const full = useCapability("money:full");
  const perShow = useCapability("money:per_show");
  const perPerson = useCapability("money:per_person");
  const hasShowMoney = full || perShow || perPerson;
  const pay = useOpsCalendarMyPay(kind, id, hasShowMoney && !!detail.myStatus);
  const payLine = payText(pay.data?.amountCents, pay.data?.perDiemCents);

  const kindKey = detail.kind ?? "show";
  // ART-328 — an opportunity names its own type ("PR · Placement"); a show still on hold says so.
  const kindText = detail.itemKind === "opportunity" && detail.opportunityType
    ? oppTypeLabel({ type: detail.opportunityType, prSubtype: detail.prSubtype })
    : detail.itemKind === "show" && detail.isHold ? "Show · hold" : (KIND_LABEL[kindKey] ?? "Event");
  const placement = detail.opportunityType === "pr" && detail.prSubtype === "placement";
  // An invite the viewer was ASKED to (they hold an answer row) — the 05 Details: the You block, Who else's count,
  // and Can't make it · I'm in (Pass · Approve on a placement), or their answer + Change answer once given.
  const answer = detail.myResponse === "in" || detail.myResponse === "out" ? detail.myResponse : null;
  const invitee = detail.myResponse != null;
  const [changing, setChanging] = useState(false);
  const replyDays = offerDaysLeft(detail.replyBy);
  // ART-305 v3 — once a show is played the pill says PLAYED, then COMPLETED when its payments are closed out.
  const played = !!detail.played;
  const pill = played
    ? detail.completed ? { label: "Completed", tone: "going" as const } : { label: "Played", tone: "itinerary" as const }
    : detail.myStatus ? STATUS_PILL[detail.myStatus] : undefined;
  const moneyView = played && !!detail.canSettle;
  const box = useShowSettleBox(detail, moneyView).data;
  const titleText = detail.title ?? KIND_LABEL[kindKey] ?? "Event";
  const name = detail.notifyName?.trim() || null;
  const times = detail.times ?? [];
  const first = times[0] ? formatTime12(times[0].time) : null;
  const last = times.length > 1 ? formatTime12(times[times.length - 1]!.time) : null;
  const timeFrame = first ? (last && last !== first ? `${first} – ${last}` : first) : null;
  const directions = detail.venueName || detail.address
    ? mapsUrl({ query: [detail.venueName, detail.address].filter(Boolean).join(", ") })
    : null;
  const people = detail.people ?? [];
  const busy = respond.isPending || hide.isPending;

  const actionRow = actionsFor(detail);

  function confirmed() {
    if (confirm === "hide") {
      hide.mutate({ id: id }, {
        onSuccess: () => { onClose(); pushToast("Deleted from your calendar.", "success"); },
      });
      return;
    }
    respond.mutate({ kind, id: id, response: "out" }, {
      onSuccess: () => {
        onClose();
        pushToast(`You backed out of ${titleText}.${name ? ` ${name} was notified.` : ""}`, "success");
      },
    });
  }

  // The viewer's own Calendar expenses on this show ("anyone can … view the expenses they've added", PO) — asked
  // only on a show they are going to. A money seat sees every expense in Settlement instead.
  const onShow = detail.itemKind === "show" && detail.myStatus === "going";
  // Played ⇒ "Your pay" lists them (non-money) or Money does (money seats).
  const mine = useMyShowExpenses(detail.showId ?? undefined, onShow && !detail.canSettle && !played);
  const myExpenses = mine.data?.items ?? [];

  const footer = (node: ReactNode) => node && <footer className={styles.actions}>{node}</footer>;

  const top = stack[stack.length - 1];
  if (top) {
    const prev = stack[stack.length - 2];
    const backTo = BACK_LABEL[prev?.view ?? "main"] ?? "Details";
    return (
      <>
        <div className={styles.subBar}>
          <button type="button" className={styles.back} onClick={pop}>
            <Icon icon={ChevronLeft} size={20} />
            {backTo}
          </button>
          <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
            <Icon icon={X} size={20} />
          </button>
        </div>
        {top.view === "expense" ? (
          <AddExpenseView detail={detail} onDone={pop} footer={footer} />
        ) : top.view === "money" ? (
          <MoneyView
            detail={detail}
            isMobile={isMobile}
            footer={footer}
            onAddExpense={detail.canAddExpense ? () => push({ view: "expense" }) : null}
            onOpenSettlement={(s) => push({
              view: "settle",
              show: s.showId === detail.showId ? undefined : { showId: s.showId ?? "", venue: s.venue ?? "", date: s.date ?? "" },
            })}
          />
        ) : (
          <SettlementView
            detail={top.show ? { ...detail, showId: top.show.showId, venueName: top.show.venue, title: top.show.venue, date: top.show.date } : detail}
            footer={footer}
            onAddExpense={detail.canAddExpense && !top.show ? () => push({ view: "expense" }) : null}
          />
        )}
      </>
    );
  }

  function openExpense() {
    push({ view: "expense" });
  }

  const confirmCopy = confirm === "backout"
    ? `Back out of ${titleText}? ${name ? `${name} gets a notification and your spot opens back up.` : "Your spot opens back up."}`
    : "Remove this from your calendar? The tour itinerary stays the same for everyone else.";

  return (
    <>
      <div className={styles.scroll}>
        <header className={styles.head}>
          <div className={styles.headRow}>
            <span className={`${styles.kindSquare} ${styles.kind}`} data-kind={kindKey} aria-hidden />
            <span className={styles.kindLabel}>{kindText}</span>
            {detail.replyBy && !played ? (
              <span className={styles.pill} data-tone={replyDays != null && replyDays <= 3 ? "urgent" : "invited"}>
                Reply by {formatDay(detail.replyBy)}
              </span>
            ) : pill ? (
              <span className={styles.pill} data-tone={pill.tone}>{pill.label}</span>
            ) : null}
            <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
              <Icon icon={X} size={20} />
            </button>
          </div>
          <h2 className={styles.title}>{titleText}</h2>
          <p className={styles.date}>{detail.date ? longDate(detail.date) : "No date set"}</p>
        </header>

        {(timeFrame || detail.dosAvailable) && (
          <div className={styles.timeBar}>
            {timeFrame && (
              <span className={styles.timeFrame}>
                <Icon icon={Clock} size={16} />
                <span>{timeFrame}</span>
              </span>
            )}
            {detail.dosAvailable && detail.showId && (
              <button
                type="button"
                className={styles.dos}
                onClick={() => {
                  onClose();
                  void navigate({ to: "/dos", search: { show: detail.showId!, via: "link" } });
                }}
              >
                Open DOS <Icon icon={ArrowUpRight} size={16} />
              </button>
            )}
          </div>
        )}

        <div className={styles.sections}>
          {(detail.venueName || detail.address) && (
            <Section icon={MapPin} label="Location">
              {detail.venueName && <span className={styles.strong}>{detail.venueName}</span>}
              {detail.address && <span className={styles.sub}>{detail.address}</span>}
              {directions && (
                <a className={styles.link} href={directions} target="_blank" rel="noreferrer">Directions</a>
              )}
            </Section>
          )}

          {played && !moneyView && detail.myStatus === "going" && <YourPayCard kind={kind} id={id} />}

          {moneyView && <MoneySummaryCard detail={detail} box={box} />}

          {invitee && !played ? (
            <YouBlock detail={detail} payLine={payLine} payAsked={hasShowMoney} moneyNotes={pay.data?.moneyNotes ?? null} />
          ) : !played && payLine ? (
            <Section icon={Banknote} label="Your pay">
              <span className={styles.payLine}>{payLine}</span>
            </Section>
          ) : null}

          {(detail.prSubtype || detail.prFormat || detail.meetingLink) && (
            <Section icon={Megaphone} label="PR">
              {detail.prSubtype && <span><span className={styles.sub}>PR type </span><span className={styles.strong}>{prSubtypeLabel(detail.prSubtype)}</span></span>}
              {detail.prFormat && <span><span className={styles.sub}>Format </span><span className={styles.strong}>{prFormatLabel(detail.prFormat)}</span></span>}
              {detail.meetingLink && (
                <a className={styles.link} href={detail.meetingLink} target="_blank" rel="noreferrer">Join</a>
              )}
            </Section>
          )}

          {myExpenses.length > 0 && (
            <Section icon={Receipt} label="Your expenses">
              <ul className={styles.myExpenses}>
                {myExpenses.map((x) => (
                  <li key={x.lineId}>
                    <span>{x.label}{x.receiptMediaId ? " · receipt" : ""}</span>
                    <span className={styles.strong}>{dollars(x.amountCents ?? 0)}</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {people.length > 0 && !placement && (
            <Section icon={Users} label="Who's there" chips>
              {invitee && whoElseCount(people) && <span className={styles.countLine}>{whoElseCount(people)}</span>}
              <ul className={styles.people} aria-label="Who's there">
                {people.map((p) => <PersonChip key={p.personId} person={p} />)}
              </ul>
            </Section>
          )}

          {detail.teamNote && (
            <section className={styles.teamNote} aria-label="Note to the team">
              <span className={styles.teamNoteFrom}>
                {[detail.teamNoteBy?.name, ago(detail.teamNoteOn)].filter(Boolean).join(" · ")}
              </span>
              <span>{detail.teamNote}</span>
            </section>
          )}

          {detail.note && <p className={styles.note}>{detail.note}</p>}
        </div>
      </div>

      {invitee && !played && !detail.canAddExpense && !detail.canSettle ? (
        <InviteFooter
          detail={detail}
          answer={answer}
          changing={changing}
          placement={placement}
          busy={busy}
          onChange={() => setChanging(true)}
          onAnswer={(response) => respond.mutate({ kind, id, response }, {
            onSuccess: () => {
              setChanging(false);
              pushToast(response === "in"
                ? (name ? `You're in. ${name} can see you accepted.` : "You're in.")
                : (name ? `You're out. ${name} can see your answer.` : "You're out."), "success");
            },
          })}
        />
      ) : actionRow && (
        <footer className={styles.actions} data-confirm={confirm ? "" : undefined}>
          {confirm ? (
            <>
              <p className={styles.confirmCopy}>{confirmCopy}</p>
              <div className={styles.buttons}>
                <button type="button" className={styles.btnGhost} onClick={() => setConfirm(null)} disabled={busy}>
                  Cancel
                </button>
                <button type="button" className={styles.btnDanger} onClick={confirmed} disabled={busy}>
                  {confirm === "backout" ? "Back out" : "Delete"}
                </button>
              </div>
            </>
          ) : actionRow === "going" && played ? (
            moneyView ? (
              detail.completed ? (
                <>
                  <p className={styles.helper}>{completedLine(box)}</p>
                  <div className={styles.buttons}>
                    <ReopenScopeButton detail={detail} />
                    <button type="button" className={styles.btnPrimary} onClick={() => push({ view: "money" })}>Money</button>
                  </div>
                </>
              ) : (
                <div className={styles.buttons}>
                  {detail.canAddExpense && (
                    <button type="button" className={styles.btnGhost} onClick={openExpense}>
                      <Icon icon={Plus} size={16} /> Add expense
                    </button>
                  )}
                  <button type="button" className={styles.btnPrimary} onClick={() => push({ view: "money" })}>Money</button>
                </div>
              )
            ) : detail.canAddExpense ? (
              <div className={styles.buttons}>
                <button type="button" className={styles.btnPrimary} onClick={openExpense}>
                  <Icon icon={Plus} size={16} /> Add expense
                </button>
              </div>
            ) : detail.completed ? (
              <ExpensesClosedBar />
            ) : null
          ) : actionRow === "going" ? (
            detail.canSettle ? (
              // A money seat on a show: Add expense · Settlement, and backing out demoted to a quiet link.
              <>
                <div className={styles.buttons}>
                  {detail.canAddExpense && (
                    <button type="button" className={styles.btnGhost} onClick={openExpense}>+ Add expense</button>
                  )}
                  <button type="button" className={styles.btnPrimary} onClick={() => push({ view: "settle" })}>Settlement</button>
                </div>
                {detail.canRespond && (
                  <button type="button" className={styles.textLink} onClick={() => setConfirm("backout")} disabled={busy}>
                    Back out of this show
                  </button>
                )}
              </>
            ) : (
              <div className={styles.buttons}>
                {detail.canRespond && (
                  <button type="button" className={styles.btnGhost} onClick={() => setConfirm("backout")} disabled={busy}>
                    Back out
                  </button>
                )}
                {detail.canAddExpense && (
                  <button type="button" className={styles.btnPrimary} onClick={openExpense}>+ Add expense</button>
                )}
              </div>
            )
          ) : (
            <div className={styles.buttons}>
              <button type="button" className={styles.btnGhost} onClick={() => setConfirm("hide")} disabled={busy}>
                Delete
              </button>
            </div>
          )}
        </footer>
      )}
    </>
  );
}

/** Which button row the viewer gets — straight from the served standing. A show they are going to keeps a row
 *  after it has passed (no Back out then) while its expenses and settlement can still be worked. An INVITE's
 *  answer is not here: it is `InviteFooter`'s (ART-328). */
function actionsFor(d: Detail): "going" | "itinerary" | null {
  if (d.myStatus === "itinerary") return "itinerary";
  if (d.myStatus === "going" && (d.canRespond || d.canAddExpense || d.canSettle || d.played)) return "going";
  return null;
}

function Section({ icon, label, children, chips }: {
  icon: typeof MapPin;
  label: string;
  children: ReactNode;
  /** The body opens with a row of person chips, so the icon centres on that row rather than a text line. */
  chips?: boolean;
}) {
  return (
    <section className={styles.section} aria-label={label}>
      <span className={chips ? `${styles.sectionIcon} ${styles.sectionIconChips}` : styles.sectionIcon} aria-hidden>
        <Icon icon={icon} size={16} />
      </span>
      <div className={styles.sectionBody}>{children}</div>
    </section>
  );
}

const STATUS_WORD: Record<string, string> = { going: "Going", invited: "Invited", out: "Out" };

function PersonChip({ person }: { person: Person }) {
  const status = person.status ?? "going";
  const full = person.name ?? "";
  const label = person.isMe ? "You" : (person.firstName || full.split(" ")[0] || "Someone");
  const hover = [full, person.role, STATUS_WORD[status]].filter(Boolean).join(" · ");
  return (
    <li className={styles.chip} data-status={status} title={hover} aria-label={hover}>
      <span className={styles.ring}>
        <PersonAvatar personId={person.personId} avatarMediaId={person.avatarMediaId} initials={initials(full)} size="xs" />
      </span>
      <span className={styles.chipName}>{label}</span>
    </li>
  );
}

/** "Friday, October 16, 2026" from a zoneless `YYYY-MM-DD` (local noon, so the device's zone wins). */
function longDate(iso: string): string {
  return new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString(undefined, {
    weekday: "long", month: "long", day: "numeric", year: "numeric",
  });
}

function dollars(cents: number): string {
  return (cents / 100).toLocaleString(undefined, {
    style: "currency", currency: "USD", minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  });
}

/** The pay line — only what is stored, amount + per diem joined: "$450 + Per diem $50". Null ⇒ no section. */
function payText(amountCents: number | null | undefined, perDiemCents: number | null | undefined): string | null {
  const parts: string[] = [];
  if (amountCents != null && amountCents > 0) parts.push(dollars(amountCents));
  if (perDiemCents != null && perDiemCents > 0) parts.push(`Per diem ${dollars(perDiemCents)}`);
  return parts.length ? parts.join(" + ") : null;
}

/** ART-328 — the You block on an invite: your pay big ("Unpaid" when the invite carries none), then your role, your
 *  set and the money notes when there are any. Only ever the viewer's own figures. */
function YouBlock({ detail, payLine, payAsked, moneyNotes }: {
  detail: Detail; payLine: string | null; payAsked: boolean; moneyNotes: string | null;
}) {
  const set = [slotRoleLabel(detail.setSlot), detail.setMinutes ? `${detail.setMinutes} min` : null].filter(Boolean).join(" · ");
  const unpaid = payAsked && !payLine;
  if (!payLine && !unpaid && !detail.myRole && !set && !moneyNotes) return null;
  return (
    <section className={styles.you} aria-label="You">
      {(payLine || unpaid) && (
        <div className={styles.youPay}>
          <span className={styles.youLabel}>Your pay</span>
          <span className={styles.youAmount}>{payLine ?? "Unpaid"}</span>
        </div>
      )}
      {detail.myRole && <YouRow label="Your role" value={detail.myRole} />}
      {set && <YouRow label="Set" value={set} />}
      {moneyNotes && <YouRow label="Money notes" value={moneyNotes} />}
    </section>
  );
}

function YouRow({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.youRow}>
      <span className={styles.sub}>{label}</span>
      <span className={styles.strong}>{value}</span>
    </div>
  );
}

/** "2 in · 1 waiting · 1 out" over everyone on the invite. */
function whoElseCount(people: readonly Person[]): string | null {
  const n = (s: string) => people.filter((p) => (p.status ?? "going") === s).length;
  const parts = [
    n("going") ? `${n("going")} in` : null,
    n("invited") ? `${n("invited")} waiting` : null,
    n("out") ? `${n("out")} out` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
}

/** ART-328 — an invite's footer. Unanswered: Can't make it · I'm in (Pass · Approve on a placement), with "It's a
 *  hold, not confirmed yet" above while the show is a hold. Answered: the answer, and Change answer while it can
 *  still change. Past the reply-by (or the date) it is read-only. */
function InviteFooter({ detail, answer, changing, placement, busy, onChange, onAnswer }: {
  detail: Detail;
  answer: "in" | "out" | null;
  changing: boolean;
  placement: boolean;
  busy: boolean;
  onChange: () => void;
  onAnswer: (response: "in" | "out") => void;
}) {
  const asking = detail.canRespond && (answer == null || changing);
  if (!asking) {
    if (!answer) return null;
    return (
      <footer className={styles.actions}>
        <div className={styles.answered}>
          <span className={styles.answerText} data-answer={answer}>
            {placement ? (answer === "in" ? "You approved" : "You passed") : answer === "in" ? "You're in" : "You're out"}
          </span>
          {detail.canChangeAnswer && (
            <button type="button" className={styles.btnGhost} onClick={onChange} disabled={busy}>Change answer</button>
          )}
        </div>
      </footer>
    );
  }
  return (
    <footer className={styles.actions}>
      {detail.isHold && <p className={styles.helper}>It&apos;s a hold, not confirmed yet.</p>}
      <div className={styles.buttons}>
        <button type="button" className={styles.btnGhost} onClick={() => onAnswer("out")} disabled={busy}>
          {placement ? "Pass" : "Can't make it"}
        </button>
        <button type="button" className={styles.btnPrimary} onClick={() => onAnswer("in")} disabled={busy}>
          {placement ? "Approve" : "I'm in"}
        </button>
      </div>
    </footer>
  );
}
